import { FormikProps } from "formik";
import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Option } from "types";
import * as Yup from "yup";

import { VALIDATION_TYPES } from "@/components/Formik";
import { Header } from "@/components/Header";
import { IConsultantProfileDto, IConsultantProfileSkill } from "@/data/consultant-profiles";
import { ISkill } from "@/data/skills";
import { Flex, FloatedList, Group } from "@/eworkitComponents";
import { isErrorVisible } from "@/formikComponents";
import { SkillBadge, SuggestedSkillsSection } from "@/views/components/ConsultantProfile/Skills";
import { SkillCreatableTypeahead } from "@/views/components/SkillCreatableTypehead";

export interface IProfileSkillsSectionValues {
  skills: IConsultantProfileSkill[];
}

export const profileSkillsSectionValidationSchema = (required?: boolean) =>
  Yup.object()
    .required()
    .shape({
      skills: Yup.array().test({
        message: VALIDATION_TYPES.REQUIRED,
        test: (val: IConsultantProfileSkill[]) => (required ? val.length > 0 : true),
      }),
    });

interface IProfileSkillsSectionProps {
  form: FormikProps<IProfileSkillsSectionValues>;
  profile: IConsultantProfileDto;
  required?: boolean;
}

export const ProfileSkillsSection = memo((props: IProfileSkillsSectionProps) => {
  const { t } = useTranslation("session");

  const values = props.form.values;
  const profileRole = props.profile.roles?.[0] && props.profile.roles[0].role;
  const cvIds = useMemo(() => props.profile.cvs?.map(cv => cv.id), []);

  const handleSkillClick = useCallback(
    (skillId: number) => {
      props.form.setFieldValue("skills", [...values.skills.filter(s => s.skill.id !== skillId)]);
    },
    [props.form],
  );

  const handleSkillsChange = useCallback(
    (value?: IConsultantProfileSkill[]) => {
      const newSkillsValue = value ? value.map(s => ({ skill: s })) : [];
      props.form.setFieldValue("skills", [...values.skills, ...newSkillsValue]);
    },
    [props.form],
  );

  const handleSuggestedSkillClick = useCallback(
    (skill: ISkill) => {
      props.form.setFieldValue("skills", [...values.skills, { skill }]);
    },
    [props.form],
  );

  const handleCheckedSuggestedSkillClick = useCallback(
    (skill: ISkill) => {
      props.form.setFieldValue("skills", [...values.skills.filter(v => v.skill.id !== skill.id)]);
    },
    [props.form],
  );

  const filterOption = (option: Option<ISkill>, _: string) => !values.skills.find(v => v.skill.id === option.data.id);

  return (
    <Flex direction="column" innerMargin="sm">
      <Header size="sm" color="black" weight="bold">
        {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.HEADER_SKILLS")}
      </Header>

      <Flex grow={1} direction="column" innerMargin="md">
        <Group
          direction="column"
          required={props.required}
          label={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.LABEL_SKILLS")}
          width={{ xxs: 12 }}>
          <SkillCreatableTypeahead
            name="skills"
            testName="skills"
            labelKey="name"
            valueKey="id"
            onChange={handleSkillsChange}
            filterOption={filterOption}
            isMulti
            placeholder={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_SKILLS")}
            invalid={!!props.form.errors.skills && !!isErrorVisible(props.form, "skills")}
          />
        </Group>

        {values.skills.length > 0 && (
          <FloatedList>
            {values.skills.map(skill => (
              <SkillBadge key={skill.skill.id} skill={skill.skill} onClick={handleSkillClick} />
            ))}
          </FloatedList>
        )}

        <SuggestedSkillsSection
          onSkillClick={handleSuggestedSkillClick}
          onCheckedSkillClick={handleCheckedSuggestedSkillClick}
          selectedSkills={values.skills}
          roleId={profileRole?.id}
          cvIds={cvIds}
        />
      </Flex>
    </Flex>
  );
});
